<template>
  <div
    data-test-id="item-modifier-set"
  >
    <q-expansion-item
      v-if="isExpandable"
      expand-icon-toggle
      hide-expand-icon
      :model-value="expanded"
    >
      <template #header>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1">
            {{ itemModifierSet.modifierSet.name }}
          </q-item-label>
          <q-item-label
            caption
            lines="2"
          >
            <span
              :class="{
                'text-positive': isValid && itemModifierSet.minSelectionCount,
                'text-negative': !isValid && itemModifierSet.minSelectionCount,
                'text-warning': !itemModifierSet.minSelectionCount
              }"
            >
              <q-icon
                v-if="!itemModifierSet.minSelectionCount"
                name="fa-regular fa-circle-question"
              />
              <q-icon
                v-else
                :name="isValid ? 'fa-regular fa-circle-check' : 'fa-regular fa-triangle-exclamation'"
              />
              {{ itemModifierSet.minSelectionCount ? $t('common.required') : $t('common.optional') }} </span> • {{ isValid ? selectedModifierNames : itemModifierSet.choiceCountDescription }}
          </q-item-label>
        </q-item-section>

        <q-item-section
          side
          top
        >
          <q-btn
            v-if="!expanded || (expanded && isValid)"
            :label="expanded ? 'Done' : 'Change'"
            dense
            :color="$register ? 'primary' : ''"
            :class="!$register ? 'text-underline' : ''"
            no-caps
            :flat="!$register"
            @click="expanded = !expanded"
          />
        </q-item-section>
      </template>
      <template #default>
        <div
          class="row"
          :class="$q.screen.gt.xs ? 'q-col-gutter-lg' : 'q-col-gutter-sm'"
        >
          <div
            v-for="modifier in availableModifiers"
            :key="modifier.id"
            class="col-12 col-sm-6"
            :class="{ 'col-lg-4': round }"
          >
            <q-card
              flat
              bordered
              class="position-relative q-pa-md rounded-borders-8"
              :class="{
                'bg-responsive-grey-2': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id),
                'text-red': errorMessage,
              }"
              :style="{
                'border-radius': round ? '24px' : undefined,
                'border-width': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? '2px' : undefined,
                'border-color': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? currentSite.primaryColor : undefined
              }"
              @click="handleClick(modifier)"
            >
              <q-card-section
                horizontal
                class="justify-between items-center q-pa-sm"
                :style="!editMode && hasReducedOpacity(modifier, { item, itemOrderItem, orderItem }) ? 'opacity: 0.3;' : ''"
              >
                <q-icon
                  :name="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? `fas fa-${round ? 'circle' : 'square'}-check` : `fal fa-${round ? 'circle' : 'square'}`"
                  :color="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? 'primary' : errorMessage ? 'red' : 'responsive'"
                  :class="{ 'bg-white': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) }"
                  style="height: 20px;"
                  class="col-auto q-mr-sm"
                  size="sm"
                />
                <q-img
                  v-if="modifier.image"
                  :ratio="1"
                  class="rounded-borders q-mr-sm"
                  :src="modifier.imageUrl"
                  :style="{
                    width: $q.screen.lt.sm? '20px' : '30px',
                    height: $q.screen.lt.sm? '20px' : '30px',
                    'background-color': modifier.color
                  }"
                />
                <div
                  class="col"
                >
                  <div
                    class="text-subtitle2"
                    style="line-height: 1.5;"
                  >
                    {{ modifier.name }}
                  </div>

                  <div
                    v-if="modifier.description"
                    class="text-caption"
                    style="line-height: 1;"
                  >
                    {{ modifier.description }}
                  </div>
                </div>

                <slot
                  name="price"
                  :modifier="modifier"
                >
                  <div
                    v-if="modifier.price"
                    class="col-auto"
                    style="min-width: 50px;"
                  >
                    +
                    <i18n-n
                      :value="modifier.price"
                      tag="span"
                      class="text-subtitle2"
                      format="currency"
                      scope="global"
                    />
                  </div>
                </slot>
              </q-card-section>
              <q-badge
                v-if="isAvailabilityBadgeVisible(modifier, { item, itemOrderItem, orderItem })"
                class="absolute-top-right"
                :color="availabilityBadgeColor(modifier, { item, itemOrderItem, orderItem })"
                :label="availableStock(modifier, { item, itemOrderItem, orderItem })"
              />
            </q-card>
          </div>
        </div>
      </template>
    </q-expansion-item>
    <div
      v-else
      class="relative-position"
      :class="{
        'text-red': errorMessage,
        'q-mb-md': $q.screen.gt.xs,
        'q-mb-sm': !$q.screen.gt.xs
      }"
    >
      <div class="text-h5 row justify-between">
        {{ itemModifierSet.modifierSet.name }}
        <template v-if="$register">
          <q-btn
            v-if="!editMode"
            label="Edit Options"
            icon="edit"
            outline
            :loading="configuration.loading"
            @click="$emit('edit-options-button-click', () => toggleEditMode())"
          />
        </template>
      </div>
      <div>
        <template v-if="itemModifierSet.minSelectionCount">
          <b>{{ $t('common.required') }}</b> <span class="q-mx-xs"> &middot;</span>
        </template>
        <template v-if="errorMessage">
          <q-icon
            name="error"
            color="red"
            size="xs"
          /> {{ errorMessage }}
        </template>
        <template v-else>
          {{ itemModifierSet.choiceCountDescription }}
        </template>
      </div>
    </div>

    <div
      v-if="!isExpandable"
      :style="editMode ? 'border: 2px solid var(--q-info);' : ''"
    >
      <div
        v-if="editMode"
        class="full-width row items-center q-pa-sm q-mb-md bg-info"
        :class="$isColorDark(currentSite.infoColor) ? 'text-white' : 'text-black'"
      >
        <div class="col">
          <div class="row items-center">
            <q-icon
              class="col-auto q-mr-md"
              size="md"
              name="info"
            />
            <div class="col">
              <div class="text-h5">
                Editing Sellable Modifiers
              </div>
              <div>
                Toggle each modifier to enable (on) or disable (off) sales.
              </div>
            </div>
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            class="q-mr-md"
            label="Cancel"
            icon="cancel"
            outline
            color="negative"
            @click="toggleEditMode()"
          />
          <q-btn
            label="Save & Close"
            icon="save"
            color="primary"
            :loading="savingEdits || configuration.loading"
            :disable="!hasEdits"
            @click="handleSaveEdits"
          />
        </div>
      </div>
      <div
        class="row"
        :class="$q.screen.gt.xs ? 'q-col-gutter-md' : 'q-col-gutter-sm'"
      >
        <div
          v-for="modifier in availableModifiers"
          :key="modifier.id"
          class="col-12 col-sm-6"
          :class="{ 'col-lg-4': round }"
        >
          <q-card
            flat
            bordered
            class="position-relative"
            :class="{
              'bg-responsive-grey-2': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id),
              'text-red': errorMessage
            }"
            :style="{
              'border-radius': round ? '24px' : undefined,
              'border-width': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? '2px' : undefined,
              'border-color': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? currentSite.primaryColor : undefined
            }"
            @click="handleClick(modifier)"
          >
            <q-card-section
              horizontal
              class="justify-between items-center q-pa-sm"
              :style="!editMode && hasReducedOpacity(modifier, { item, itemOrderItem, orderItem }) ? 'opacity: 0.3;' : ''"
            >
              <q-icon
                :name="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? `fas fa-${round ? 'circle' : 'square'}-check` : `fal fa-${round ? 'circle' : 'square'}`"
                :color="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? 'primary' : errorMessage ? 'red' : 'responsive'"
                :class="{ 'bg-white': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) }"
                style="height: 20px;"
                class="col-auto q-mr-sm"
                size="sm"
              />
              <q-img
                v-if="modifier.image"
                :ratio="1"
                class="rounded-borders q-mr-sm"
                :src="modifier.imageUrl"
                :style="{
                  width: $q.screen.lt.sm? '20px' : '30px',
                  height: $q.screen.lt.sm? '20px' : '30px',
                  'background-color': modifier.color
                }"
              />
              <div
                class="col"
              >
                <div
                  :class="$q.screen.lt.sm ? 'text-h6' : 'text-h5'"
                  style="line-height: 1.5;"
                >
                  {{ modifier.name }}
                </div>

                <div
                  v-if="modifier.description"
                  class="text-caption"
                  style="line-height: 1;"
                >
                  {{ modifier.description }}
                </div>
              </div>

              <q-toggle
                v-if="editMode"
                :model-value="areSalesEnabled(modifier)"
                :disable="modifier.loading || configuration.loading"
                @update:model-value="toggleSalesDisabled(modifier)"
              />
              <slot
                v-else
                name="price"
                :modifier="modifier"
              >
                <div
                  v-if="modifier.price"
                  class="col-auto"
                  style="min-width: 50px;"
                >
                  <i18n-n
                    :value="modifier.price"
                    tag="span"
                    class="text-subtitle1"
                    format="currency"
                    scope="global"
                  />
                </div>
              </slot>
            </q-card-section>
            <q-badge
              v-if="isAvailabilityBadgeVisible(modifier, { item, itemOrderItem, orderItem })"
              class="absolute-top-right"
              :color="availabilityBadgeColor(modifier, { item, itemOrderItem, orderItem })"
              :label="availableStock(modifier, { item, itemOrderItem, orderItem })"
            />
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFormChild } from 'quasar'
import Model from 'src/models/model'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { useAvailableModifiers } from 'src/modules/checkout/composables/sales-availability/modifiers/AvailableModifiers'
import { useSalesChannel } from 'src/modules/checkout/composables/sales-channels/SalesChannel'
import { useDisableSales } from 'src/modules/register/composables/disable-sales'
import { useSalesRestrictions } from 'src/modules/register/composables/sales-restrictions/sales-restrictions'
import { availabilityBadgeColor, availableStock, hasReducedOpacity, isAvailabilityBadgeVisible } from 'src/services/inventory/sales-restrictions'
import { nextTick, ref, toRefs, watchEffect } from 'vue'

export default {
  name: 'ItemModifierSet',
  configuration: ['currentSite'],
  context: ['currentUser'],
  props: {
    orderItem: {
      type: Object,
      required: true
    },
    itemOrderItem: {
      type: Object,
      required: true
    },
    itemModifierSet: {
      type: Object,
      requred: true,
      default: null
    },
    availableOnline: {
      type: Boolean,
      required: false,
      default: false
    },
    isExpandable: {
      type: Boolean,
      required: false,
      default: false
    },
    round: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['request-authorization', 'edit-options-button-click'],
  setup (props) {
    const { salesChannel } = useSalesChannel(
      SALES_CHANNEL_TYPES.MENU,
      props.orderItem?.order
    )

    const { availableModifiers } = useAvailableModifiers(
      props.itemModifierSet?.modifierSet?.modifiers?.data,
      {
        salesChannel,
        orderItem: props.orderItem
      }
    )

    const { checkForSalesRestrictions } = useSalesRestrictions()

    const { itemOrderItem, itemModifierSet } = toRefs(props)

    const validating = ref(false)
    const errorMessage = ref(null)

    watchEffect(() => {
      if (validating.value) {
        errorMessage.value = itemOrderItem.value.validateForModifierSet(itemModifierSet.value)
      } else {
        errorMessage.value = null
      }
    })

    async function validate () {
      validating.value = true
      await nextTick()
      return !errorMessage.value
    }

    useFormChild({ validate })

    const {
      editMode,
      toggleEditMode,
      areSalesEnabled,
      toggleSalesDisabled,
      hasEdits,
      saveEdits
    } = useDisableSales()

    return {
      availableModifiers,
      errorMessage,
      editMode,
      toggleEditMode,
      areSalesEnabled,
      toggleSalesDisabled,
      hasEdits,
      saveEdits,
      // Sales Restrictions
      checkForSalesRestrictions
    }
  },
  data () {
    return {
      validating: false,
      expanded: false,
      savingEdits: false
    }
  },
  computed: {
    item () {
      return this.itemOrderItem.item
    },
    isValid () {
      if (this.itemModifierSet.minSelectionCount) {
        return _.filter(this.itemOrderItem.itemModifiers.data, itemModifier => itemModifier.modifierSet.id === this.itemModifierSet.modifierSet.id).length >= this.itemModifierSet.minSelectionCount
      }

      return true
    },
    selectedModifierNames () {
      const modifierNames = _(this.itemOrderItem.itemModifiers.data)
        .filter(itemModifier => itemModifier.modifierSet.id === this.itemModifierSet.modifierSet.id)
        .map(itemModifier => itemModifier.modifier.name)
        .compact()
        .value()

      if (modifierNames.length === 0) return this.$t('common.noneSelected')

      return modifierNames.join(', ')
    }
  },
  watch: {
    isValid () {
      this.expanded = !this.isValid
    }
  },
  created () {
    _.extend(this, {
      configuration: Model.configuration,
      availabilityBadgeColor,
      availableStock,
      hasReducedOpacity,
      isAvailabilityBadgeVisible
    })
  },
  methods: {
    async handleSaveEdits () {
      try {
        const selectedModifiers = _.filter(this.availableModifiers, modifier => this.itemOrderItem.hasModifier(modifier, this.itemModifierSet.modifierSet.id))
        const modifiersToUnselect = _.filter(selectedModifiers, modifier => !this.areSalesEnabled(modifier))

        this.savingEdits = true
        await this.saveEdits()

        if (!_.isEmpty(modifiersToUnselect)) {
          _.each(modifiersToUnselect, this.toggleModifier)
        }
      } catch (e) {
        this.$rollbar.error(`Failed to save disable sales edits, exception: ${e.message}`)
      } finally {
        this.savingEdits = false
      }
    },
    toggleModifier (modifier) {
      this.itemOrderItem.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
      if (this.itemModifierSet.minSelectionCount === 1 && this.isValid) {
        this.expanded = false
      }
    },
    handleClick (modifier) {
      if (this.editMode) return

      if (this.itemOrderItem.hasModifier(modifier, this.itemModifierSet.modifierSetId)) {
        this.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
        return
      }

      this.checkForSalesRestrictions(
        modifier,
        { item: this.item, itemOrderItem: this.itemOrderItem, orderItem: this.orderItem },
        (adminApproved = false) => {
          this.itemOrderItem.adminApproved = adminApproved
          this.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
        }
      )
    }
  }
}

</script>
